import { useState } from 'react';

interface Document {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadDate: string;
  category: string;
  icon: string;
}

export default function Documents() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [documents, setDocuments] = useState<Document[]>([
    {
      id: '1',
      name: 'Auto Insurance Policy - 2024',
      type: 'PDF',
      size: '2.4 MB',
      uploadDate: '2024-11-01',
      category: 'policy',
      icon: 'ri-file-pdf-line'
    },
    {
      id: '2',
      name: 'Home Insurance Certificate',
      type: 'PDF',
      size: '1.8 MB',
      uploadDate: '2024-10-28',
      category: 'policy',
      icon: 'ri-file-pdf-line'
    },
    {
      id: '3',
      name: 'Claim Form - Auto Accident',
      type: 'PDF',
      size: '956 KB',
      uploadDate: '2024-11-10',
      category: 'claim',
      icon: 'ri-file-pdf-line'
    },
    {
      id: '4',
      name: 'Vehicle Registration',
      type: 'JPG',
      size: '1.2 MB',
      uploadDate: '2024-09-15',
      category: 'personal',
      icon: 'ri-image-line'
    },
    {
      id: '5',
      name: 'Driver License Copy',
      type: 'JPG',
      size: '887 KB',
      uploadDate: '2024-09-15',
      category: 'personal',
      icon: 'ri-image-line'
    },
    {
      id: '6',
      name: 'Life Insurance Beneficiary Form',
      type: 'PDF',
      size: '1.1 MB',
      uploadDate: '2024-08-22',
      category: 'policy',
      icon: 'ri-file-pdf-line'
    }
  ]);

  const categories = [
    { id: 'all', label: 'All Documents', count: documents.length },
    { id: 'policy', label: 'Policy Documents', count: documents.filter(d => d.category === 'policy').length },
    { id: 'claim', label: 'Claims', count: documents.filter(d => d.category === 'claim').length },
    { id: 'personal', label: 'Personal Documents', count: documents.filter(d => d.category === 'personal').length }
  ];

  const filteredDocuments = selectedCategory === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === selectedCategory);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // Handle file upload logic here
      console.log('Files to upload:', files);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-upload-line mr-2"></i>
          Upload Document
        </button>
      </div>

      {/* Storage Usage */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Storage Usage</h3>
          <span className="text-sm text-gray-500">8.2 MB used of 100 MB</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full" style={{ width: '8.2%' }}></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Category Filter */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Categories</h3>
            </div>
            <div className="p-4">
              <ul className="space-y-2">
                {categories.map((category) => (
                  <li key={category.id}>
                    <button
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors cursor-pointer ${
                        selectedCategory === category.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{category.label}</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {category.count}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {categories.find(c => c.id === selectedCategory)?.label}
                </h3>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-layout-grid-line"></i>
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-list-unordered"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-file-line text-4xl text-gray-300 mb-4"></i>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
                  <p className="text-gray-500 mb-4">Upload your first document to get started.</p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Upload Document
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredDocuments.map((document) => (
                    <div key={document.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <i className={`${document.icon} text-2xl text-blue-600`}></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{document.name}</h4>
                          <p className="text-sm text-gray-500">
                            {document.type} • {document.size} • Uploaded {new Date(document.uploadDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-blue-600 cursor-pointer" title="Download">
                          <i className="ri-download-line"></i>
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer" title="Preview">
                          <i className="ri-eye-line"></i>
                        </button>
                        <button className="p-2 text-gray-400 hover:text-red-600 cursor-pointer" title="Delete">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Upload Document</h3>
            </div>
            <div className="p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Drop files here or click to upload
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports PDF, JPG, PNG, DOC files up to 10MB each
                  </p>
                </label>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8">
                  <option value="policy">Policy Documents</option>
                  <option value="claim">Claims</option>
                  <option value="personal">Personal Documents</option>
                </select>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                Upload Files
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}